import type { Estado } from "../api/prisma";
import estilos from "./mesa.module.css";

/** Etapas do workflow na ordem em que o servidor registra o estado (servidor/workflow.py). */
export const ETAPAS: { estado: Estado; nome: string; quem: string }[] = [
  { estado: "recebida", nome: "Tirar dados pessoais", quem: "código" },
  { estado: "preparada", nome: "Separar a afirmação e procurar a origem", quem: "modelo + código" },
  { estado: "consultando", nome: "Consultar a fonte oficial", quem: "código" },
  {
    estado: "julgando",
    nome: "Júri: três votos independentes",
    quem: "modelo · unanimidade é código",
  },
  { estado: "redigindo", nome: "Escrever o card e as leituras", quem: "modelo" },
  { estado: "conferindo", nome: "Conferir cada citação na fonte", quem: "código + modelo" },
];

export type Situacao = "feito" | "agora" | "a fazer";

/**
 * Situação de cada etapa a partir do estado registrado, que é o da etapa que está rodando. A
 * privacidade roda no recebimento, então a primeira já chega feita; com o pedido ainda na fila
 * ("recebida"), a próxima é a que vem. Nada aqui vem de relógio.
 */
export function situacoes(estado: Estado): Situacao[] {
  if (estado === "concluida") return ETAPAS.map(() => "feito");
  const indice = ETAPAS.findIndex((e) => e.estado === estado);
  const rodando = indice <= 0 ? 1 : indice;
  return ETAPAS.map((_, i) =>
    i === 0 || i < rodando ? "feito" : i === rodando ? "agora" : "a fazer",
  );
}

/**
 * O "checando" como rastro de agente (Princípio VII à vista: quem é código, quem é modelo).
 * Aberto enquanto roda; recolhido quando termina, e a pessoa pode reabrir.
 */
export function Rastro({ estado }: { estado: Estado }) {
  const pronto = estado === "concluida";
  const lista = situacoes(estado);
  const atual = ETAPAS[lista.indexOf("agora")] ?? ETAPAS[ETAPAS.length - 1];
  const feitas = lista.filter((s) => s === "feito").length;
  return (
    <>
      <details className={estilos.rastro} open={!pronto}>
        <summary>
          {pronto ? (
            <span className={estilos.ok} aria-hidden="true">
              ✓
            </span>
          ) : (
            <span className={estilos.giro} aria-hidden="true" />
          )}
          <span className={estilos.rastroTitulo}>
            {pronto ? `Checado em ${String(ETAPAS.length)} etapas` : `${atual?.nome ?? ""}…`}
          </span>
          <span className={estilos.rastroMeta}>
            {pronto ? "ver etapas" : `${String(feitas)}/${String(ETAPAS.length)}`}
          </span>
        </summary>
        <ol>
          {ETAPAS.map((e, i) => {
            const s = lista[i] ?? "a fazer";
            return (
              <li key={e.estado} data-situacao={s}>
                <span aria-hidden="true">
                  {s === "feito" ? "✓" : s === "agora" ? <span className={estilos.giro} /> : "○"}
                </span>
                <span>
                  {e.nome}
                  <span className="visualmente-oculto">
                    {s === "feito" ? ": feito" : s === "agora" ? ": em andamento" : ": a fazer"}
                  </span>
                </span>
                <span className={estilos.quem}>{e.quem}</span>
              </li>
            );
          })}
        </ol>
      </details>
      {/* Fora do <summary>: leitor de tela anuncia a troca de etapa sem abrir o rastro. */}
      <p role="status" className="visualmente-oculto">
        {pronto ? "Análise concluída." : `Etapa atual: ${atual?.nome ?? ""}.`}
      </p>
    </>
  );
}
